import React, { useEffect, useState, useMemo, useRef } from 'react';
import { 
  listUsers, deleteUser, reactivateUser, deactivateUser, getRoles, updateUserRole
} from '../../services/userManagement/userManagement.service';
import { formatDateTime } from '../../helpers/formatDateTime';
import { DateTimeDisplay, StatusMessage, ActionList, Modal, Breadcrumbs } from "../../components/ui";
import { StatusKind } from '../../components/ui/StatusMessage';
import SortIcon from '../../components/ui/Sorting';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import type { UserListItem, UserRole } from '../../services/userManagement/userManagement.types';
import { ModalContent } from '../../components/ui/ModalContent';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

type SortOrder = 'asc' | 'desc';
type UserSortColumn = 'name' | 'email' | 'employeeId' | 'role' | 'status' | 'createdAt' | 'updatedAt';

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return String(error);
};

const getAriaSort = (
  activeColumn: UserSortColumn,
  activeOrder: SortOrder,
  column: UserSortColumn
): 'ascending' | 'descending' | 'none' => {
  if (activeColumn !== column) {
    return 'none';
  }

  if (activeOrder === 'asc') {
    return 'ascending';
  }

  return 'descending';
};

const getSortIconOrder = (
  activeColumn: UserSortColumn,
  activeOrder: SortOrder,
  column: UserSortColumn
): SortOrder | null => {
  if (activeColumn !== column) {
    return null;
  }

  return activeOrder;
};

const getUserStatusLabel = (user: UserListItem): 'Deleted' | 'Active' | 'Inactive' => {
  if (user.deletedOn) {
    return 'Deleted';
  }

  if (user.isActive) {
    return 'Active';
  }

  return 'Inactive';
};

const getDisplayName = (user: UserListItem): string => {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (fullName) {
    return fullName;
  }

  return '—';
};

interface EditUserModalContentProps {
  selectedUser: UserListItem | null;
  editFields: {
    role: string;
  };
  setEditFields: React.Dispatch<React.SetStateAction<{
    role: string;
  }>>;
  roles: string[];
}

const EditUserModalContent: React.FC<EditUserModalContentProps> = ({
  selectedUser,
  editFields,
  setEditFields,
  roles
}) => {
  if (!selectedUser) {
    return null;
  }

  return (
    <div className="edit-user-modal">
      <div className="edit-user-modal__grid">

        <div className="edit-user-modal__field edit-user-modal__field--full">
          <label className="edit-user-modal__label" htmlFor="admin-edit-user-role">Role</label>
          <select
            id="admin-edit-user-role"
            className="edit-user-modal__select"
            value={editFields.role}
            onChange={(e) => setEditFields(prev => ({ ...prev, role: e.target.value }))}
          >
            {roles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
};

const Admin: React.FC = () => {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [globalRoles, setGlobalRoles] = useState<UserRole[]>([]);

  const breadcrumbs = useBreadcrumbs({ currentPageTitle: 'Admin - User Management' });

  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [editFields, setEditFields] = useState({ role: "" });

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [openModalCommonConfirm, setOpenModalCommonConfirm] = useState(false);
  const [modalHeadingText, setModalHeadingText] = useState<string | undefined>(undefined);
  const [modalMessageText, setModalMessageText] = useState<string | undefined>(undefined);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const [showResponseSummary, setShowResponseSummary] = useState(false);
  const [typeResponse, setTypeResponse] = useState<StatusKind | undefined>(undefined);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);

  const roles = globalRoles.map(role => role.name);
  const handleCloseResponseMessage = () => setShowResponseSummary(false);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, rolesRes] = await Promise.all([listUsers(), getRoles()]);
      if (usersRes?.success && usersRes.data) {
        setUsers(usersRes.data);
      } else {
        setError(usersRes?.error?.message ?? "Failed to load users");
      }

      if (rolesRes?.success && rolesRes.data) {
        setGlobalRoles(rolesRes.data);
      } else {
        setError(rolesRes?.error?.message ?? "Failed to load roles");
      }
    } catch (e: unknown) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const updateUserData = async (localData: { role: string; roleId?: number }) => {
    if (!selectedUser) {
      return;
    }

    setLoading(true);
    
    const roleIdValue = globalRoles.find(r => r.name === localData.role)?.id;
    const roleId = typeof roleIdValue === 'number' ? roleIdValue : Number(roleIdValue);
    
    try {
        // Only update role for other users
        if (!Number.isNaN(roleId) && localData.role !== selectedUser.role) {
          const roleUpdate = await updateUserRole(selectedUser.id, roleId);
          if (roleUpdate?.success) {
            setShowResponseSummary(true);
            setResponseMessage("User Role Updated");
            setTypeResponse('info');
            await loadUsers();
          } else {
            setShowResponseSummary(true);
            setResponseMessage(roleUpdate?.error?.message ?? "Failed to update role");
            setTypeResponse('error');
          }
        } else {
          setShowResponseSummary(true);
          setResponseMessage("No changes to save");
          setTypeResponse('info');
        }
        window.scrollTo(0, 0);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleEditConfirm = () => {

    if (!selectedUser) {
      return;
    }

    const hasNoChanges = editFields.role === selectedUser.role;

    if (hasNoChanges) {
      setOpenEditModal(false);
      return;
    }

    setConfirmAction(() => async () => {
      setOpenModalConfirm(false);
      await updateUserData(editFields);
    });

    setOpenModalConfirm(true);
  };

  const waitForModalConfirm = (): Promise<boolean> => {
    return new Promise(resolve => {
      resolverRef.current = resolve;
      setOpenModalCommonConfirm(true);
    });
  };

  const confirmAndActivateUser = async (userId: string, isActive: boolean) => {
    const action = isActive ? 'deactivate' : 'activate';
    setModalHeadingText('Deactivate User');
    setModalMessageText(`Are you sure you want to ${action} this user?`);
    const confirmed = await waitForModalConfirm();
    if (confirmed) {
      if (isActive) {
        await handleDeactivateUser(userId);
      } else {
        await handleReactivateUser(userId);
      }
    }
  };

  const confirmAndDeleteUser = async (userId: string) => {
    setModalHeadingText('Delete User');
    setModalMessageText(`Are you sure you want to delete this user?`);
    const confirmed = await waitForModalConfirm();
    if (confirmed) {
      await handleDeleteUser(userId);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    setActionLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await deleteUser(userId);
      if (response?.success) {
        setShowResponseSummary(true);
        setResponseMessage('User deleted successfully');
        setTypeResponse('info');
        await loadUsers();
      } else {
        setShowResponseSummary(true);
        setResponseMessage(response?.error?.message ?? 'Failed to delete user');
        setTypeResponse('error');
      }
    } catch (e: unknown) {
      setShowResponseSummary(true);
      setResponseMessage(getErrorMessage(e));
      setTypeResponse('error');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    setActionLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await deactivateUser(userId);
      if (response?.success) {
        setShowResponseSummary(true);
        setResponseMessage("User deactivated successfully");
        setTypeResponse('info');
        await loadUsers();
      } else {
        setError(response?.error?.message ?? 'Failed to deactivate user');
      }
    } catch (e: unknown) {
      setError(getErrorMessage(e));
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleReactivateUser = async (userId: string) => {
    setActionLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await reactivateUser(userId);
      if (response?.success) {
        setShowResponseSummary(true);
        setResponseMessage('User reactivated successfully');
        setTypeResponse('info');
        await loadUsers();
      } else {
        setShowResponseSummary(true);
        setResponseMessage(response?.error?.message ?? 'Failed to reactivate user');
        setTypeResponse('error');
      }
    } catch (e: unknown) {
      setError(getErrorMessage(e));
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const [sortColumn, setSortColumn] = useState<UserSortColumn>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const handleSort = (column: UserSortColumn) => {
    setSortColumn(column);
    setSortOrder(prev => {
      if (sortColumn !== column) {
        return 'asc';
      }

      if (prev === 'asc') {
        return 'desc';
      }

      return 'asc';
    });
  };

  const sortedUsers = useMemo(() => {
    if (!users || users.length === 0) {
      return users;
    }
    const copy = [...users];
    const compareStrings = (a?: string | null, b?: string | null) => {
      const sa = (a ?? '').toLowerCase();
      const sb = (b ?? '').toLowerCase();
      return sa.localeCompare(sb);
    };

    const compareDates = (a?: string | null, b?: string | null) => {
      const ta = a ? new Date(a).getTime() : 0;
      const tb = b ? new Date(b).getTime() : 0;
      return ta - tb;
    };

    const mult = sortOrder === 'asc' ? 1 : -1;

    copy.sort((x, y) => {
      let res = 0;
      switch (sortColumn) {
        case 'name':
          res = compareStrings([x.firstName, x.lastName].filter(Boolean).join(' '), [y.firstName, y.lastName].filter(Boolean).join(' '));
          break;
        case 'email':
          res = compareStrings(x.email, y.email);
          break;
        case 'employeeId':
          res = compareStrings(x.employeeId, y.employeeId);
          break;
        case 'role':
          res = compareStrings(x.role, y.role);
          break;
        case 'status':
          res = compareStrings(x.isActive ? 'active' : 'inactive', y.isActive ? 'active' : 'inactive');
          break;
        case 'createdAt':
          res = compareDates(x.createdAt, y.createdAt);
          break;
        case 'updatedAt':
          res = compareDates(x.updatedAt, y.updatedAt);
          break;
        default:
          res = compareStrings([x.firstName, x.lastName].filter(Boolean).join(' '), [y.firstName, y.lastName].filter(Boolean).join(' '));
      }
      return res * mult;
    });

    return copy;
  }, [users, sortColumn, sortOrder]);

  if (loading) {
    return (
    <main>
      <div className="container">
        <section className='page'>
          <h2 className="heading__title heading__title--admin">Admin - User Management</h2>
          <DateTimeDisplay />
          <Breadcrumbs items={breadcrumbs} />
        </section>
        <section className="page-section"><LoadingSpinner message="Loading users…" /></section>
      </div>
    </main>
  );
  }
  if (error) {
    return (
    <main>
      <div className="container">
        <section className='page'>
          <h2 className="heading__title heading__title--admin">Admin - User Management</h2>
          <DateTimeDisplay />
          <Breadcrumbs items={breadcrumbs} />
        </section>
        <section className="page-section">
          <div>Error loading users: {error}</div>
          <button className="btn btn--secondary" onClick={loadUsers}>Retry</button>
        </section>
      </div>
    </main>
  );}

  return (
    <main>
      <div className="container">

        <Modal
          isOpen={openEditModal}
          title="Edit Role"
          content={
            <EditUserModalContent
              selectedUser={selectedUser}
              editFields={editFields}
              setEditFields={setEditFields}
              roles={roles}
            />
          }
          onConfirm={handleEditConfirm}
          onCancel={() => {
            setOpenEditModal(false);
          }}
        />

        <Modal
          isOpen={openModalConfirm}
          title="Confirm Changes"
          content={<ModalContent heading="Confirm Update" message="Are you sure you want to apply these changes?" />}
          onConfirm={() => {
            if (confirmAction) {
              confirmAction();
              setOpenModalConfirm(false);
              setOpenEditModal(false);
            }
          }}
          onCancel={() => setOpenModalConfirm(false)}
        />

        <Modal
          isOpen={openModalCommonConfirm}
          title={modalHeadingText ?? ''}
          headerClass="modal__header--danger"
          content={<ModalContent heading={modalHeadingText} message={modalMessageText} />}
          onConfirm={() => {
            resolverRef.current?.(true);
            setOpenModalCommonConfirm(false);
          }}
          onCancel={() => {
            resolverRef.current?.(false);
            setOpenModalCommonConfirm(false);
          }}
        />

        <section className='page'>
          <h2 className="heading__title heading__title--admin">Admin - User Management</h2>
          <DateTimeDisplay />
          <Breadcrumbs items={breadcrumbs} />
        </section>

        <section className="alarm-content mb-2 p-1">
          {responseMessage && showResponseSummary && (
            <StatusMessage kind={typeResponse} onClose={handleCloseResponseMessage}>
              {responseMessage}
            </StatusMessage>
          )}
          <h3 className="heading__title heading__title--users mb-1 mt-0">System Users ({users.length})</h3>
          {users.length > 0 ? (
            <table className="table-responsive">
              <thead>
                <tr>
                  <th onClick={() => { handleSort('name'); }} aria-sort={getAriaSort(sortColumn, sortOrder, 'name')} className="th--sortable">
                    Name <SortIcon sortOrder={getSortIconOrder(sortColumn, sortOrder, 'name')} />
                  </th>
                  <th onClick={() => { handleSort('email'); }} aria-sort={getAriaSort(sortColumn, sortOrder, 'email')} className="th--sortable">
                    Email <SortIcon sortOrder={getSortIconOrder(sortColumn, sortOrder, 'email')} />
                  </th>
                  <th onClick={() => { handleSort('employeeId'); }} aria-sort={getAriaSort(sortColumn, sortOrder, 'employeeId')} className="th--sortable">
                    Employee ID <SortIcon sortOrder={getSortIconOrder(sortColumn, sortOrder, 'employeeId')} />
                  </th>
                  <th onClick={() => { handleSort('role'); }} aria-sort={getAriaSort(sortColumn, sortOrder, 'role')} className="th--sortable">
                    Role <SortIcon sortOrder={getSortIconOrder(sortColumn, sortOrder, 'role')} />
                  </th>
                  <th onClick={() => { handleSort('status'); }} aria-sort={getAriaSort(sortColumn, sortOrder, 'status')} className="th--sortable">
                    Status <SortIcon sortOrder={getSortIconOrder(sortColumn, sortOrder, 'status')} />
                  </th>
                  <th onClick={() => { handleSort('createdAt'); }} aria-sort={getAriaSort(sortColumn, sortOrder, 'createdAt')} className="th--sortable">
                    Created <SortIcon sortOrder={getSortIconOrder(sortColumn, sortOrder, 'createdAt')} />
                  </th>
                  <th onClick={() => { handleSort('updatedAt'); }} aria-sort={getAriaSort(sortColumn, sortOrder, 'updatedAt')} className="th--sortable">
                    Last Updated <SortIcon sortOrder={getSortIconOrder(sortColumn, sortOrder, 'updatedAt')} />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(sortedUsers ?? users).map(user => (
                  <tr key={user.id}>
                    <td>
                      {getDisplayName(user)}
                    </td>
                    <td>
                      {user.email ? (
                        <a href={`mailto:${user.email}`} className="alarm-name-link">
                          {user.email}
                        </a>
                      ) : '—'}
                    </td>
                    <td>{user.employeeId ?? '—'}</td>
                    <td>
                      {user.role ?? 'Not Set'}
                    </td>
                    <td>
                      <span className={`status ${user.isActive ? 'status__active' : 'status__inactive'}`}>
                        {getUserStatusLabel(user)}
                      </span>
                    </td>
                    <td>{formatDateTime(user.createdAt)}</td>
                    <td>{formatDateTime(user.updatedAt)}</td>
                    <td>
                      <div className="actions">
                        <div className="actions__list">
                          {user.deletedOn ? (
                            <ActionList
                              customActions={[
                                {
                                  label: 'Reactivate',
                                  onClick: () => {
                                    void handleReactivateUser(user.id);
                                  },
                                  disabled: actionLoading[user.id],
                                  className: 'actions__link--reactivate'
                                }
                              ]}
                              disabled={actionLoading[user.id]}
                            />
                          ) : (
                            <ActionList
                              customActions={[
                                {
                                  label: 'Edit Role',
                                  onClick: () => {
                                    setSelectedUser(user);
                                    setEditFields({
                                      role: user.role ?? ''
                                    });
                                    setOpenEditModal(true);
                                  },
                                  disabled: actionLoading[user.id],
                                  className: 'actions__link--edit',
                                  itemClassName: 'actions__item--edit'
                                },
                                {
                                  label: user.isActive ? 'Deactivate' : 'Activate',
                                  onClick: () => {
                                    void confirmAndActivateUser(user.id, !!user.isActive);
                                  },
                                  disabled: actionLoading[user.id],
                                  className: user.isActive ? 'actions__link--deactivate' : 'actions__link--activate',
                                  itemClassName: user.isActive ? 'actions__item--deactivate' : 'actions__item--activate'
                                },
                                {
                                  label: 'Delete',
                                  onClick: () => {
                                    void confirmAndDeleteUser(user.id);
                                  },
                                  disabled: actionLoading[user.id],
                                  className: 'actions__link--delete',
                                  itemClassName: 'actions__item--delete'
                                }
                              ]}
                              disabled={actionLoading[user.id]}
                            />
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <p>No users found in the system.</p>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default Admin;